import React, { useCallback, useEffect, useMemo } from 'react';

import Button from 'components/form/Button.component';
import KnobInput from 'components/form/KnobInput.component';
import CalendarInput from 'components/generic/CalendarInput.component';
import WidgetTitle from 'components/widget/WidgetTitle.component';
import useResettingState from 'hooks/useResettingState.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import getString from 'utils/accessors/getString.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { timeAdd07days } from 'utils/date/manipulate.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import { isTimeEqual } from 'utils/predicates/time/compare.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import C from 'utils/values/constant.util';

import CN from './WidgetRepeatUntil.module.scss';

const L = logger('WidgetRepeatUntil');

const TIME_OFF_REPEAT_UNTIL = C({
  never: { id: 'never', description: 'Never', value: 'never' },
  variable: { id: 'variable' },
});

const OPTIONS = Object.freeze(Object.values(TIME_OFF_REPEAT_UNTIL));

const useResettingOffsetDate = (referenceDate, modifier = alwaysSame) => {
  const [previousIso, setPreviousIso] = useStateWhileMounted(referenceDate);
  const areSame = isTimeEqual(previousIso, referenceDate);
  const [value, setValue] = useStateWhileMounted(fromAnyDateToIso(modifier(referenceDate)) || null);

  useEffect(() => {
    // only reset is previous one is different from the current one
    if (areSame) return;
    setPreviousIso(referenceDate);
    setValue(fromAnyDateToIso(modifier(referenceDate)) || null);
  }, [areSame, modifier, previousIso, referenceDate, setPreviousIso, setValue]);

  return useMemo(() => ({ val: value, set: setValue, isReset: !areSame }), [areSame, setValue, value]);
};

// TODO: make one KnobCloudInput component to use instead both similar CategoryTimeOff and WidgetRepeatPeriod
const WidgetRepeatUntil = ({ name: nameFromProps, tag: tagFromProps, onChange = alwaysNoop, referenceDate, selected: selectedFromProps, autoclosePicker, disabled }) => {
  const { t } = useTranslation();
  const { formatDate } = useGilt();
  const name = fromAnyToTrimmed(nameFromProps);
  const tag = fromAnyToTrimmed(tagFromProps);

  const initSelected = asObject(selectedFromProps);
  const initSelectedId = initSelected.id || TIME_OFF_REPEAT_UNTIL.never.id;

  const { val: value, set: setValue } = useResettingOffsetDate(referenceDate, timeAdd07days);
  const [selectedId, setSelectedId] = useResettingState(initSelectedId);
  const { val: isPickerOpen, tip: togglePicker, nay: closePicker } = useTip(false);

  // copy options so they can be safely changed and remove the hidden ones
  const copies = asArray(OPTIONS)
    .map((option) => ({ ...option }))
    .filter((o) => !o.hide);

  // now update the one that is a year after the reference date
  const variableOption = copies.find((o) => o.id === TIME_OFF_REPEAT_UNTIL.variable.id);
  variableOption.value = value;
  variableOption.description = formatDate(value, { hideTime: true });

  const isVariableSelected = TIME_OFF_REPEAT_UNTIL.variable.id === selectedId;
  const isPickerRendered = isVariableSelected && isPickerOpen;

  const handleClick = useCallback(
    (ev) => {
      const v = fromAnyDateToIso(getString('raw.value', ev));

      const isSelected = selectedId === ev.name;
      const id = isSelected ? null : ev.name;
      const raw = isSelected ? null : v || null;

      L.debug('handleClick()', { v, raw });
      const returned = asObject(call(`WidgetRepeatUntil.onChange`, onChange, { raw, name, tag }));

      setSelectedId(isMissing(returned.selected) ? id : returned.selected);
      setValue(isMissing(returned.value) ? raw : returned.value);
    },
    [name, onChange, selectedId, setSelectedId, setValue, tag],
  );

  const handlePick = useCallback(
    (ev) => {
      const v = fromAnyDateToIso(asObject(ev).raw);
      const raw = v || null;

      L.debug('handlePick()', { v, raw });
      const returned = asObject(call(`WidgetRepeatUntil.onChange`, onChange, { raw, name, tag }));

      setValue(isMissing(returned.value) ? raw : returned.value);
      if (autoclosePicker) closePicker();
    },
    [autoclosePicker, closePicker, name, onChange, setValue, tag],
  );

  return (
    <div data-bem="WidgetRepeatUntil" data-tag={tag} className={CN.component}>
      <div className={CN.titleContainer}>
        <WidgetTitle tag="WidgetRepeatUntil__title">{t('scheduler.timeOff.repeatEndTitle')}</WidgetTitle>
      </div>

      <div data-bem="WidgetRepeatUntil__container" className={CN.container}>
        <div data-bem="WidgetRepeatUntil__list" className={CN.list}>
          {copies.map((option) => (
            <KnobInput
              key={createNodeKey({ tag: option.description, unique: option.id })}
              name={option.id}
              tag="WidgetRepeatUntil__item"
              value={option}
              selected={selectedId === option.id}
              onClick={handleClick}
              disabled={disabled}
            >
              {option.description}
            </KnobInput>
          ))}
        </div>

        <Button tag="WidgetRepeatUntil__edit" disabled={!isVariableSelected || disabled} variant="slim" onClick={togglePicker}>
          {t('scheduler.timeOff.editDateButton')}
        </Button>
      </div>

      {isPickerRendered && (
        <div data-bem="WidgetRepeatUntil__picker" className={CN.calendarContainer}>
          <CalendarInput tag="WidgetRepeatUntil__calendar" value={value} width="full" name={name} disabled={disabled} onChange={handlePick} />
        </div>
      )}
    </div>
  );
};

export default WidgetRepeatUntil;
